<template>
  <div class="item-group-list-container item-group-list-main item-group-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Item Categories']" />

    <div class="glass-tab-view glass-tab-view--flush item-group-list-glass-shell">
      <div class="glass-tab-view__content item-group-list-tab-content item-group-list-page-stack">
        <div class="item-group-list-header-block">
          <div class="item-group-list-header-row">
            <div class="item-group-list-header-row__text">
              <h2>Item Categories</h2>
              <p class="item-group-list-header-row__sub">
                Categories group items within each item type (Product, Service, Inventory) for lists and reports
              </p>
            </div>
            <div class="item-group-list-header-actions">
              <LabelDropdown
                label="Item Type"
                select-id="item-category-type-filter"
                v-model="selectedItemType"
                inline
                :options="itemTypeOptions"
                @change="fetchGroups"
              />
              <PrimaryButton label="Add New" @click="showModal = true" />
            </div>
          </div>
        </div>

        <div class="item-group-list-glass-panel item-group-list-main-card">
          <div class="consultant-glass-table-section item-group-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table item-group-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Item Type</th>
                      <th class="item-group-list-delete-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="3" class="consultant-glass-table-no-records item-group-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="3" class="consultant-glass-table-no-records item-group-list-no-records">{{ error }}</td>
                    </tr>
                    <tr v-else-if="groups.length === 0">
                      <td colspan="3" class="consultant-glass-table-no-records item-group-list-no-records">
                        No categories found.
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(group, idx) in groups"
                        :key="group.id || idx"
                        class="consultant-glass-table-row item-group-list-row"
                        :style="{ '--igl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>{{ group.name }}</td>
                        <td>{{ group.itemType || 'All types' }}</td>
                        <td class="item-group-list-delete-cell">
                          <SecondaryButton
                            label="Delete"
                            icon="bi bi-trash"
                            custom-class="consultant-secondary-btn--danger item-group-list-delete-btn"
                            :disabled="isDeleting"
                            @click="openDeleteModal(idx)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewItemGroup
      :visible="showModal"
      :companyId="companyId"
      :item-type="selectedItemType || null"
      @ok="handleItemGroupCreated"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Category"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeleteGroup"
    />
  </div>
</template>

<script>
import '../styles/ItemGroupList.css';
import AddNewItemGroup from './AddNewItemGroup.vue';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';

export default {
  name: 'ItemGroupList',
  components: {
    AddNewItemGroup,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
    LabelDropdown,
  },
  data() {
    return {
      showModal: false,
      groups: [],
      selectedItemType: '',
      itemTypeOptions: [
        { value: '', label: 'All types' },
        { value: 'Product', label: 'Product' },
        { value: 'Service', label: 'Service' },
        { value: 'Inventory', label: 'Inventory' },
      ],
      loading: false,
      error: '',
      groupToDeleteIdx: null,
      companyId: this.$route.params.id,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      isDeleting: false,
    };
  },
  computed: {
    hasGroups() {
      return this.groups && this.groups.length > 0;
    },
    canDelete() {
      return this.hasGroups && !this.isDeleting;
    },
  },
  async mounted() {
    await this.fetchGroups();
  },
  methods: {
    async fetchGroups() {
      this.loading = true;
      this.error = '';
      try {
        this.groups = await CompanyService.getAllItemGroups(this.companyId, this.selectedItemType || null);
        logger.log('Successfully fetched item groups:', this.groups.length);
      } catch (err) {
        logger.error('Error fetching item groups:', err);

        if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Item groups service not found. Please contact support.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to view item groups.';
        } else if (err.response?.status === 500) {
          this.errorMessage =
            'Internal server error occurred while fetching item groups. The server encountered an unexpected condition. Please try again in a few minutes.';
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while fetching item groups. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'An unexpected error occurred while fetching item groups. Please try again.';
        }
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.error = '';
    },
    async handleItemGroupCreated(createdGroup) {
      this.closeModal();
      await this.fetchGroups();
      const groupCount = this.groups.length;
      this.successMessage = `Item group "${createdGroup?.name || 'New Item Group'}" created successfully! You now have ${groupCount} item group${groupCount !== 1 ? 's' : ''} in your system.`;
      this.showSuccessModal = true;
      logger.log('Item group created successfully, total groups:', groupCount);
    },
    openDeleteModal(idx) {
      const group = this.groups[idx];
      if (!group || !group.id) {
        this.errorMessage = 'Invalid item group selected. Please refresh the page and try again.';
        this.showErrorModal = true;
        return;
      }
      if (!group.name || group.name.trim() === '') {
        this.errorMessage = 'Cannot delete an item group with an empty name.';
        this.showErrorModal = true;
        return;
      }
      this.groupToDeleteIdx = idx;
      this.warningMessage = `Are you sure you want to delete the item group "${group.name}"? This action cannot be undone and may affect items assigned to this group.`;
      this.showWarningModal = true;
      logger.log('Delete confirmation requested for item group:', group.name);
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.groupToDeleteIdx = null;
      this.warningMessage = '';
    },
    async confirmDeleteGroup() {
      if (this.groupToDeleteIdx === null) return;

      this.isDeleting = true;
      this.showWarningModal = false;

      try {
        const group = this.groups[this.groupToDeleteIdx];
        logger.log('Attempting to delete item group:', group?.name, 'ID:', group?.id);

        await CompanyService.deleteItemGroup(group.id);
        await this.fetchGroups();

        const remainingCount = this.groups.length;
        this.successMessage = `Item group "${group?.name}" has been deleted successfully. You now have ${remainingCount} item group${remainingCount !== 1 ? 's' : ''} remaining.`;
        this.showSuccessModal = true;
        logger.log('Item group deleted successfully, remaining groups:', remainingCount);
      } catch (err) {
        logger.error('Error deleting item group:', err);

        const errorText =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          '';

        if (
          errorText.includes('foreign key constraint') ||
          errorText.includes('violates foreign key') ||
          errorText.includes('referenced by') ||
          errorText.includes('cannot delete') ||
          errorText.includes('constraint') ||
          errorText.includes('items') ||
          errorText.includes('inventory') ||
          errorText.includes('itemGroupId') ||
          errorText.includes('ItemGroups')
        ) {
          const group = this.groups[this.groupToDeleteIdx];
          if (errorText.includes('items') || errorText.includes('item')) {
            this.errorMessage = `Cannot delete "${group?.name}" because it is currently assigned to one or more items. Please reassign those items to another group first.`;
          } else if (errorText.includes('inventory')) {
            this.errorMessage = `Cannot delete "${group?.name}" because it is currently assigned to one or more inventory items. Please reassign those items to another group first.`;
          } else {
            this.errorMessage = `Cannot delete "${group?.name}" because it is currently being used by other records. Please reassign those records to another group first.`;
          }
        } else if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 409) {
          const group = this.groups[this.groupToDeleteIdx];
          this.errorMessage = `Cannot delete "${group?.name}" because it is currently assigned to one or more items. Please reassign those items to another group first.`;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Item group not found. It may have already been deleted.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to delete item groups.';
        } else if (err.response?.status === 500) {
          const errText =
            err.response?.data?.message || err.response?.data?.error || err.response?.data?.detail || '';
          if (
            errText.includes('foreign key constraint') ||
            errText.includes('violates foreign key') ||
            errText.includes('referenced by') ||
            errText.includes('items') ||
            errText.includes('inventory') ||
            errText.includes('itemGroupId') ||
            errText.includes('ItemGroups')
          ) {
            const group = this.groups[this.groupToDeleteIdx];
            this.errorMessage = `Cannot delete "${group?.name}" because it is currently assigned to one or more items. Please reassign those items to another group first.`;
          } else {
            this.errorMessage =
              'Internal server error occurred while deleting the item group. The server encountered an unexpected condition. Please try again in a few minutes.';
          }
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while deleting the item group. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          const group = this.groups[this.groupToDeleteIdx];
          this.errorMessage = `An unexpected error occurred while deleting "${group?.name}". Please try again.`;
        }
        this.showErrorModal = true;
      } finally {
        this.isDeleting = false;
        this.groupToDeleteIdx = null;
        this.warningMessage = '';
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
